import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BullhornAuthService } from 'auth-service-lib';
import { IHamburgerMenuItem } from 'hamburger-lib';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../../services/config.service';
import { AIApps } from '../../types/AIApps';
import { IClientConfig } from '../../types/client-config';

@Component({
  selector: 'app-hamburger-wrapper',
  template: `
    <lib-hamburger
      [menuItems]="menuItems"
      menuTitle="Kyloe AI"
      homeRoute="/"
      homeTooltip="AI Assist Home"
      [(isOpen)]="isMenuOpen">
    </lib-hamburger>
  `,

  standalone: false
})
export class HamburgerWrapperComponent implements OnInit {
  isMenuOpen = false;
  menuItems: IHamburgerMenuItem[] = [];
  private clientConfig: IClientConfig | null = null;
  corpIdDisplay = '';

  constructor(private clientConfigService: ConfigService, private authService: BullhornAuthService, private router: Router) {}

  ngOnInit() {
    this.authService.getToken().subscribe({
      next: token => {
        const { corpId, namespaceId } = this.getCorpDetails(token);
        this.corpIdDisplay = corpId;

        const isNoteTaker = this.router.url.includes('notetaker');
        const appType = isNoteTaker ? AIApps.NOTETAKER : AIApps.ASSIST;
        const gatewayUrl = isNoteTaker ? environment.ntApiGatewayUrl : environment.apiGatewayUrl;

        this.clientConfigService.getFullConfigForCorpId(appType, gatewayUrl, environment.dtApiGatewayUrl, namespaceId)
          .subscribe(config => {
            this.clientConfig = config;
            this.buildMenuItems();
          });
      },
      error: err => {
        console.error('Failed to get token:', err);
      }
    });
  }

  private getCorpDetails(token: string): { corpId: string; namespaceId: string } {
    try {
      const jwt = jwtDecode<any>(token);
      const corpId = jwt.corporationId || '';
      const namespaceId = `bullhorn-${corpId}`;
      return { corpId, namespaceId };
    } catch (error) {
      console.error('Error getting corp details:', error);
      return { corpId: '', namespaceId: '' };
    }
  }
  private buildMenuItems() {
    const isNoteTaker = this.router.url.includes('notetaker');
    this.menuItems = [];

    if (this.clientConfig?.enabled) {
      this.menuItems.push({
        label: 'AI Assist Manager',
        icon: {name: 'auto_awesome'},
        action: () => this.goToAssist(),
        tooltip: 'Open AI Assist in a new tab',
        showNewTabIcon: false,
        isCurrentSection: !isNoteTaker,
      });
    }

    if (this.clientConfig?.notetakerEnabled) {

      this.menuItems.push({
        label: 'AI NoteTaker Manager',
        icon: {name: 'auto_awesome'},
        tooltip: 'NoteTaker tools and management',
        expanded: isNoteTaker,
        subItems: [
          {
            label: 'Call recordings',
            icon: {name: 'call_log', class: 'material-symbols'},
            action: () => this.goToNotetakerRecordings(),
            tooltip: 'Open Notetaker calls list in a new tab',
            showNewTabIcon: false,
            isCurrentSection: this.router.url === '/notetaker-manager',
          },
          {
            label: 'Add questions',
            icon: {name: 'indeterminate_question_box', class: 'material-symbols'},
            action: () => this.goToNotetakerQuestions(),
            tooltip: 'Open Notetaker Questions in a new tab',
            showNewTabIcon: false,
            isCurrentSection: this.router.url === '/notetaker-manager-questions',
          }
        ]
      });
    }

    this.menuItems.push({
      label: 'Open Bullhorn',
      icon: {name: 'dashboard'},
      action: () => this.goToTenantWebsite(),
      tooltip: 'Open Bullhorn in a new tab',
      showNewTabIcon: true,
      customClass: 'external-link'
    });
  }

  private goToAssist() {
    this.router.navigate(['/']);
  }

  private goToNotetakerRecordings() {
    this.router.navigate(['/notetaker-manager']);
  }

  private goToNotetakerQuestions() {
    this.router.navigate(['/notetaker-manager-questions']);
  }

  private goToTenantWebsite() {
    window.open('https://universal.bullhornstaffing.com/universal-login/login', '_blank');
  }
}
